import { WidgetProps } from '../../models/widget.model';
import { memo, useEffect, useMemo, useState } from 'react';
import { AlignmentCell2 } from './widget-subcomponents/TreeList.subcomponents';
import { GridCellProps } from '@progress/kendo-react-grid';
import { State, process } from '@progress/kendo-data-query';
import DataGrid from '../datagrid/DataGrid';
import ProfileCell from '../flyouts/ProfileCell';
import { ListResponse } from '../../store/api/chart.api';
import { SparklineCell } from '../datagrid/SparklineCell';
import { getAlignmentProfileQuestionColumns } from 'common/components/widgets/widget-utils/widgetUtils';
import { ColumnModel } from 'common/models/widget-config.model';
import { DateCell } from '../datagrid/DateCell';
export interface QuestionType {
  id: number;
  questionName: string;
  alignmentScore?: number;
  alignmentChangeScore: number;
  alignmentColor1: string;
  alignmentColor2: string;
  alignmentLabel: string;
}

export interface ProfileData {
  profileId: number;
  profile: string;
  territoryId: number;
  chartData: any;
  questions?: QuestionType[];
  alignment: number;
}

export const endColumns: ColumnModel[] = [
  {
    field: 'accountShippingCity',
    title: 'City',
    width: '100%',
    minResizableWidth: 100,
  },
  {
    field: 'accountShippingState',
    title: 'State',
    width: '100%',
    minResizableWidth: 100,
  },
  {
    field: 'accountShippingCountry',
    title: 'Country',
    width: '100%',
    minResizableWidth: 100,
  },
  {
    field: 'accountInstitution',
    title: 'Institution',
    width: '200%',
    minResizableWidth: 200,
  },
];

export const initialColumns: ColumnModel[] = [
  {
    field: 'profile',
    title: 'Profile',
    width: '300%',
    minResizableWidth: 200,
    cell: ({ dataItem }: any) => {
      return <ProfileCell isTeamMember={false} dataItem={dataItem} selectedTab={0} />;
    },
  },
  {
    field: 'fieldTeam',
    title: 'Team Member',
    width: '200%',
    minResizableWidth: 200,
    cell: ({ dataItem }: GridCellProps) => {
      return (
        <ProfileCell
          isTeamMember={true}
          isParent={true}
          dataItem={{
            fieldTeam: dataItem.fieldTeam,
            fieldTeamId: dataItem.fieldTeamId,
            territoryId: dataItem.territoryId,
          }}
          selectedTab={0}
        />
      );
    },
  },
  {
    field: 'latestEngagement',
    title: 'Last Assessed',
    width: '200%',
    minResizableWidth: 200,
    cell: DateCell,
    cellType: 'date',
  },
  {
    field: 'alignment',
    title: 'Alignment',
    cell: AlignmentCell2,
    width: '150px',
    sortable: true,
    filter: 'numeric',
  },
  {
    field: 'volume',
    title: 'Volume',
    width: '150px',
    cell: (props: GridCellProps) => (
      <SparklineCell {...props} blueLineLabel="Responses" orangeLineLabel="Response Details" />
    ),
    sortable: false,
    filterable: false,
    columnMenu: () => null,
    headerClassName: 'alignment-profile-spakline',
  },
  {
    field: 'latestAlignmentResponse',
    title: 'Latest Alignment Response',
    filter: 'date',
    sortable: true,
    width: '150px',
    groupable: true,
    cell: DateCell,
    cellType: 'date',
  },
];

function WidgetAlignmentProfileQuestion({ data, id, groupable }: WidgetProps<ListResponse>) {
  const profileData = useMemo<ProfileData[]>(() => data.data as ProfileData[], [data.data]);
  const [filteredData, setFilteredData] = useState<ProfileData[]>(profileData);
  const [dataState, setDataState] = useState<State>(data as State);
  const [dataResult, setDataResult] = useState(process(filteredData, dataState));

  const tableColumns = useMemo(() => getAlignmentProfileQuestionColumns(data), [data]);

  // This is temporary while we wait for Bryntum to be rolled out
  const modifiedData = useMemo(() => {
    return {
      ...dataResult,
      data: dataResult.data.map((item: ProfileData) => ({
        ...item,
        questions: Array.isArray(item.questions)
          ? item.questions
          : Object.values(item.questions || []),
      })),
    };
  }, [dataResult]);

  const dataStateChange = (event: any) => {
    setDataResult(process(filteredData, event.dataState));
    setDataState(event.dataState);
  };

  useEffect(() => {
    setFilteredData(profileData);
    setDataState(data as State);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    setDataResult(process(filteredData, dataState));
  }, [filteredData, dataState]);

  return (
    <DataGrid
      widgetId={id}
      className="treelist-grid-category-response flex-grid"
      onDataStateChange={dataStateChange}
      {...dataState}
      groupable={groupable}
      total={data.total}
      sortable={true}
      data={modifiedData}
      columns={tableColumns}
    />
  );
}

export default memo(
  WidgetAlignmentProfileQuestion,
  (prevProps: any, nextProps: any) => prevProps.data.length !== nextProps.data.length
);
